import { defineComponent , inferProps, type ReactiveComponent } from "vue-utils"
import { ContentType, type HomePage, type id, type LessonPage, type Nullable, type SkillSetPage } from "../../models"
import { computed, onMounted, ref, toRaw } from "vue"
import PWAHomePage from "./content/homePage"
import PWASkillSetPage from "./content/skillSetPage"
import PWALessonPage from "./content/lessonPage"
import PageTitle from "../../components/PageTitle"
import FlashCardsPage from "./flashCards"

interface HistoryState {
   lesson: Nullable<LessonPage>
   skillSet: Nullable<SkillSetPage>
   flashCards: boolean
}

interface Props {
   content: HomePage
}

const PWAViews: ReactiveComponent<Props> = (props) => {
   const lesson = ref<LessonPage | null>(null)
   const skillSet = ref<SkillSetPage | null>(null)
   const title = ref<string>('Home')
   const flashCards = ref<boolean>(false)

   addEventListener('popstate', (e) => {
      const currentState = e.state as HistoryState | null;
      if (!currentState) return;

      const selectedPage = currentState.lesson ?? currentState.skillSet;
      setPage(selectedPage ?? undefined, currentState);
   });   

   function setPage(selectedPage?: LessonPage | SkillSetPage, state?: HistoryState) {
      switch (selectedPage?.contentType) {
         case(ContentType.lesson):
            lesson.value = selectedPage as LessonPage
            if(state)
               skillSet.value = state.skillSet
            break;
         case(ContentType.skillSet):
            lesson.value = null
            skillSet.value = selectedPage as SkillSetPage
            break;
         default:
            lesson.value = null
            skillSet.value = null
            flashCards.value = state?.flashCards ?? false
      }
      title.value = selectedPage?.name ?? "Home"
      
      if(!state)
         history.pushState({lesson: toRaw(lesson.value), skillSet: toRaw(skillSet.value), flashCards: toRaw(flashCards.value)}, toRaw(title.value), "/")
   }

   function openFlashCards() {
      flashCards.value = true
      history.pushState({lesson: toRaw(lesson.value), skillSet: toRaw(skillSet.value), flashCards: toRaw(flashCards.value)}, toRaw(title.value), "/")
   }

   function setLessonPage(next: boolean) {
      if(next) {
         const nextLesson = allLessons.value[allLessons.value.findIndex((l) => l.id === lesson.value!.id) + 1]!
         skillSet.value =  props.content.properties.lessons.includes(nextLesson) || skillSet.value?.properties.lessons.includes(nextLesson) ?
         skillSet.value :
         props.content.properties.skillSets.find((s) => s.properties.lessons.includes(nextLesson))!
         setPage(nextLesson)
      }else {
         setPage(skillSet.value ??  undefined)
      }
   }

   const allLessons = computed(() => {
      const uniqueById = new Map<id, LessonPage>();
      for (const lesson of props.content.properties.lessons.concat(props.content.properties.skillSets.flatMap((s) => s.properties.lessons))) {
         if (!uniqueById.has(lesson.id)) {
            uniqueById.set(lesson.id, lesson);
         }
      }
      return [...uniqueById.values()];
   })

   onMounted(() => {
      if(!history.state)
         history.replaceState({lesson: lesson.value, skillSet: skillSet.value, flashCards: flashCards.value}, toRaw(title.value), "/")
   })

	return () => (
      <>
      <PageTitle title={title.value}/>
		{(!lesson.value && !skillSet.value && !flashCards.value)  && (
         <PWAHomePage
            home={props.content}
            setPage={setPage}
            openFlashCards={openFlashCards}
         />
      )}
      {(!lesson.value && skillSet.value && !flashCards.value) && (
         <PWASkillSetPage
            skillSet={skillSet.value}
            setPage={setPage}
         />
      )}
      {(lesson.value && !flashCards.value) && (
         <PWALessonPage
            key={lesson.value.id}
            lesson={lesson.value}
            lastPage={allLessons.value.slice(-1)[0]?.id === lesson.value?.id}
            setPage={setLessonPage}
         />
      )}
      {flashCards.value && (
         <FlashCardsPage
            lessons={allLessons.value}
            backToHome={setPage}
         />
      )}
      </>
	)
}
export default defineComponent(PWAViews, inferProps<Props>())